import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { appointmentPublicApi, type ApiOptions, type EditorPreviewParams } from './api';
import {
  shouldShowProductPicker,
  isServiceProductRequired,
  autoSelectSingleProduct,
} from '../../lib/appointment-service-selection';
import type {
  AppointmentFormConfig,
  ServiceItem,
  ConsultantItem,
  SlotItem,
  CustomerData,
  BookingResult,
} from './types';

// Task #515 + #525: lo step "product" è stato collassato dentro lo step
// "service" e la selezione del prodotto è ora un dropdown inline sotto la
// card della tipologia (governato da `serviceSelectionMode`). Manteniamo
// il valore 'product' nel tipo solo per retrocompatibilità a runtime: NON
// è più presente in STEP_ORDER e non sarà mai impostato come step attivo.
// Task #2846 — nuovo step 'date' (scelta del giorno) PRIMA dello step
// 'consultant': in modalità 'skill_shifts' la rosa consulenti è ristretta a chi è
// in turno per il giorno scelto. Lo step 'slot' resta la scelta dell'orario.
export type WidgetStep = 'service' | 'product' | 'date' | 'consultant' | 'slot' | 'contact' | 'confirm';

export interface UseAppointmentFormOptions {
  slug: string;
  apiOptions?: ApiOptions;
  initialConfig?: AppointmentFormConfig;
  initialServices?: ServiceItem[];
  initialConsultants?: ConsultantItem[];
  preview?: boolean;
  /**
   * Task #2841 — anteprima live "viva": quando true (form già persistito), la
   * preview consuma gli endpoint pubblici REALI per consulenti e slot, così
   * rispetta le 4 modalità consulente e le regole di disponibilità lato backend
   * invece del fallback statico (filtro `consultantIds` + slot finti). La config
   * di branding/modalità continua ad arrivare in tempo reale da `initialConfig`
   * (editor); il pool e gli slot riflettono il form salvato. Su form NUOVO
   * (non salvato) resta false → fallback statico.
   */
  previewLive?: boolean;
}

// Task #525: rimosso 'product' dall'ordine degli step (selezione inline).
// Task #2846: ordine flusso = (sede, selettore in testa) → tipo/servizio → data →
// consulente → orario → contatti → conferma.
const STEP_ORDER: WidgetStep[] = ['service', 'date', 'consultant', 'slot', 'contact', 'confirm'];

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function useAppointmentForm(options: UseAppointmentFormOptions) {
  const { slug, apiOptions, initialConfig, initialServices, initialConsultants, preview, previewLive } = options;

  const [config, setConfig] = useState<AppointmentFormConfig | null>(initialConfig || null);
  const [services, setServices] = useState<ServiceItem[]>(initialServices || []);
  const [consultants, setConsultants] = useState<ConsultantItem[]>(initialConsultants || []);

  // In preview mode (live editor) the parent re-computes initialConfig/services/consultants
  // on every form change. Sync them into local state so the preview reflects edits in
  // real time instead of freezing to the values captured at first mount.
  useEffect(() => {
    if (!preview) return;
    if (initialConfig) setConfig(initialConfig);
  }, [preview, initialConfig]);
  useEffect(() => {
    if (!preview) return;
    if (initialServices) setServices(initialServices);
  }, [preview, initialServices]);
  // Task #2841 — in preview "viva" NON sincronizzare i consulenti statici
  // dall'editor: il pool reale arriva dall'endpoint pubblico (vedi sotto).
  useEffect(() => {
    if (!preview || previewLive) return;
    if (initialConsultants) setConsultants(initialConsultants);
  }, [preview, previewLive, initialConsultants]);
  const [slots, setSlots] = useState<SlotItem[]>([]);
  // Monotonic counter: solo l'ultima fetch slot può scrivere lo stato.
  const slotsRequestIdRef = useRef(0);
  const [selectedDate, setSelectedDate] = useState<string>(todayIso());
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  // Task #515: prodotto/servizio scelto (pos_products), opzionale.
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedConsultantId, setSelectedConsultantId] = useState<string>('');
  // Task #2752: sede scelta dal cliente (solo se config.allowStoreChoice).
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<SlotItem | null>(null);
  const [step, setStep] = useState<WidgetStep>('service');
  const [customer, setCustomer] = useState<CustomerData>({
    name: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    privacyAccepted: false,
    marketingAccepted: false,
    customAnswers: {},
  });
  const [honeypot, setHoneypot] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();
  const [loading, setLoading] = useState({ config: !initialConfig, services: false, consultants: false, slots: false, booking: false });
  const [error, setError] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<'failed' | 'missing' | null>(null);
  const [bookingResult, setBookingResult] = useState<BookingResult | null>(null);
  const captchaResetRef = useRef<(() => void) | null>(null);

  const registerCaptchaReset = useCallback((fn: (() => void) | null) => {
    captchaResetRef.current = fn;
  }, []);

  const resetCaptcha = useCallback(() => {
    setCaptchaToken(undefined);
    setCaptchaError(null);
    setError(null);
    try { captchaResetRef.current?.(); } catch { /* ignore */ }
  }, []);

  const setLoadingKey = useCallback((k: keyof typeof loading, v: boolean) =>
    setLoading((prev) => ({ ...prev, [k]: v })), []);

  // Load config
  useEffect(() => {
    if (initialConfig || preview) return;
    let cancelled = false;
    setLoadingKey('config', true);
    appointmentPublicApi
      .getForm(slug, apiOptions)
      .then((c) => {
        if (cancelled) return;
        setConfig(c);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoadingKey('config', false));
    return () => {
      cancelled = true;
    };
  }, [slug, apiOptions, initialConfig, preview, setLoadingKey]);

  // Load services when config arrives
  useEffect(() => {
    if (!config || preview) return;
    if (initialServices && initialServices.length) return;
    let cancelled = false;
    setLoadingKey('services', true);
    appointmentPublicApi
      .getServices(slug, apiOptions)
      .then((s) => !cancelled && setServices(s))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoadingKey('services', false));
    return () => {
      cancelled = true;
    };
  }, [config, slug, apiOptions, preview, initialServices, setLoadingKey]);

  // Task #2841 — al cambio della modalità consulente il valore selezionato va
  // azzerato. Senza questo, il sentinel 'any' (assegnazione automatica) o un id
  // reale sopravvivevano a un cambio di modalità: in anteprima viva, passando da
  // 'any' a 'manual'/'skill_shifts', la fetch slot ripartiva con consultantId
  // 'any' non più valido → 400 silenzioso → "Nessuno slot". Il confronto è sul
  // VALORE della modalità, quindi non scatta sugli altri edit di config.
  const prevFilterModeRef = useRef<AppointmentFormConfig['consultantFilterMode'] | null>(null);
  useEffect(() => {
    if (!config) return;
    const mode = config.consultantFilterMode;
    if (prevFilterModeRef.current !== null && prevFilterModeRef.current !== mode) {
      setSelectedConsultantId('');
    }
    prevFilterModeRef.current = mode;
  }, [config]);

  // Task #2786 — in modalità 'any' il cliente non sceglie: il sistema assegna
  // il consulente (sentinel 'any'). Negli altri due mode la scelta è esposta.
  useEffect(() => {
    if (!config || config.consultantFilterMode !== 'any') return;
    if (selectedConsultantId) return;
    setSelectedConsultantId('any');
  }, [config, selectedConsultantId]);

  // Task #2841 — anteprima viva: override di bozza inviati agli endpoint
  // pubblici (il backend li applica solo per richieste editor autenticate). Così
  // il pool consulenti e gli slot riflettono la modalità/roster/sede NON ancora
  // salvati invece del form persistito risolto via slug.
  const editorPreview = useMemo<EditorPreviewParams | undefined>(() => {
    if (!previewLive || !config) return undefined;
    return {
      mode: config.consultantFilterMode,
      skillFilter: config.consultantSkillFilter ?? false,
      roster: config.consultantFilterMode === 'manual' ? config.consultantIds : undefined,
      storeId: config.defaultStore?.id ?? '',
    };
  }, [previewLive, config]);

  // Load consultants. Task #2786: in modalità skill/skill_shifts il pool va
  // caricato anche SENZA servizio selezionato (mostra tutti i consulenti
  // idonei del tenant); quando il servizio è scelto il backend filtra per
  // skill. In modalità 'any' il consulente non è esposto, quindi non serve.
  // Task #2841 — la preview statica (form non salvato) salta la fetch reale e
  // usa il pool mode-aware passato dall'editor; la preview "viva" (previewLive)
  // e il widget pubblico consumano l'endpoint reale.
  useEffect(() => {
    if (!config) return;
    if (preview && !previewLive) return;
    if (config.consultantFilterMode === 'any') return;
    let cancelled = false;
    setLoadingKey('consultants', true);
    appointmentPublicApi
      // Task #515: pass selected product so backend filters consultants by
      // product-specific skills (vs union of all products of the type).
      // Task #2752: passa la sede scelta (solo se il form la consente) così il
      // backend ristringe i consulenti a quelli assegnati alla sede.
      .getConsultants(slug, selectedServiceId || '', {
        ...apiOptions,
        productId: selectedProductId || undefined,
        storeId: config.allowStoreChoice ? (selectedStoreId || undefined) : undefined,
        // Task #2846 — in 'skill_shifts' la rosa è ristretta ai consulenti in turno
        // per il giorno scelto (lo step 'date' precede 'consultant'). Negli altri
        // modi la data non vincola il pool.
        date: config.consultantFilterMode === 'skill_shifts' ? (selectedDate || undefined) : undefined,
        preview: editorPreview,
      })
      .then((c) => !cancelled && setConsultants(c))
      // In preview "viva" un errore (es. slug in fase di editing) non deve
      // sporcare l'anteprima con un banner: pool vuoto silenzioso.
      .catch((e) => {
        if (cancelled) return;
        if (preview) { setConsultants([]); return; }
        setError(e.message);
      })
      .finally(() => !cancelled && setLoadingKey('consultants', false));
    return () => {
      cancelled = true;
    };
  }, [config, selectedServiceId, selectedProductId, selectedStoreId, selectedDate, slug, apiOptions, preview, previewLive, editorPreview, setLoadingKey]);

  // Load slots when consultant + date selected
  useEffect(() => {
    if (!config || !selectedServiceId || !selectedConsultantId || !selectedDate) return;
    // In preview mode il form non esiste lato backend (id/slug = 'preview'),
    // quindi la chiamata reale fallirebbe. Generiamo slot finti deterministici
    // (09:00 → 17:30 ogni 30 min, esclusa la pausa pranzo) così l'anteprima
    // resta navigabile e mostra come si presentano gli orari nel layout.
    // Task #2841 — la preview "viva" (previewLive) usa gli slot REALI come il
    // widget pubblico; solo la preview statica (form non salvato) genera slot
    // finti.
    if (preview && !previewLive) {
      const fake: SlotItem[] = [];
      for (let h = 9; h < 18; h++) {
        if (h === 13) continue; // pausa pranzo
        for (const m of [0, 30]) {
          const start = new Date(`${selectedDate}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`);
          const end = new Date(start.getTime() + 30 * 60000);
          fake.push({ start: start.toISOString(), end: end.toISOString(), available: true });
        }
      }
      setSlots(fake);
      return;
    }
    // Task: AbortController + monotonic request id per evitare flicker quando
    // due fetch slot partono in rapida successione (es. auto-pick consulente
    // seguito da cambio data) e la seconda risposta arriva vuota: senza
    // questi guard, una risposta `[]` legittima ma stale sovrascriveva una
    // lista popolata e mostrava "Nessuno slot disponibile" pur essendoci slot.
    const reqId = ++slotsRequestIdRef.current;
    const ac = new AbortController();
    setLoadingKey('slots', true);
    appointmentPublicApi
      .getSlots(slug, {
        serviceId: selectedServiceId,
        // Task #515: include productId so the slot calc uses product-specific skills.
        productId: selectedProductId || undefined,
        // Task #2752: include la sede scelta così gli slot riflettono i consulenti della sede.
        storeId: config.allowStoreChoice ? (selectedStoreId || undefined) : undefined,
        consultantId: selectedConsultantId,
        date: selectedDate,
        preview: editorPreview,
      }, { ...apiOptions, signal: ac.signal })
      .then((s) => {
        // Solo l'ultima richiesta in ordine può scrivere lo stato. Le
        // risposte di richieste superate vengono scartate (anche se 200 OK).
        if (reqId !== slotsRequestIdRef.current) return;
        setSlots(s);
      })
      .catch((e) => {
        if (reqId !== slotsRequestIdRef.current) return;
        // AbortError è atteso quando il useEffect si re-esegue: silenzioso.
        if (e?.name === 'AbortError') return;
        // Task #2841 — in preview "viva" un errore non sporca l'anteprima:
        // nessuno slot, nessun banner d'errore.
        if (preview) { setSlots([]); return; }
        setError(e.message);
      })
      .finally(() => {
        if (reqId !== slotsRequestIdRef.current) return;
        setLoadingKey('slots', false);
      });
    return () => {
      ac.abort();
    };
  }, [config, selectedServiceId, selectedProductId, selectedStoreId, selectedConsultantId, selectedDate, slug, apiOptions, preview, previewLive, editorPreview, setLoadingKey]);

  const currentStepIndex = STEP_ORDER.indexOf(step);

  // Task #515: la lista prodotti dipende dalla tipologia selezionata.
  const productsForSelectedService = useMemo(() => {
    if (!selectedServiceId) return [] as ServiceItem['products'];
    const svc = services.find((s) => s.id === selectedServiceId);
    return svc?.products || [];
  }, [services, selectedServiceId]);

  // Task #515 + #525: governa la visibilità del SELETTORE INLINE prodotto
  // (dropdown sotto la card della tipologia). Il nome storico è mantenuto
  // per compatibilità con i layout esistenti (non si tratta più di uno step).
  const shouldShowProductStep = useCallback((): boolean => {
    if (!config) return false;
    // Task #3040 — logica condivisa col modale admin (lib/appointment-service-selection).
    return shouldShowProductPicker(config.serviceSelectionMode || 'auto', productsForSelectedService);
  }, [config, productsForSelectedService]);

  // Task #525: helper per i layout — true se la modalità impone una scelta
  // esplicita del prodotto (blocca "Avanti" finché non è selezionato).
  const isProductRequired = useCallback((): boolean => {
    if (!config) return false;
    return isServiceProductRequired(config.serviceSelectionMode || 'auto', productsForSelectedService);
  }, [config, productsForSelectedService]);

  // Task #515: la preselezione automatica del singolo prodotto è stata
  // RIMOSSA: anche con un solo servizio collegato l'utente deve poter
  // scegliere se selezionarlo o saltarlo, e la card deve apparire come
  // scelta visibile (non già "consumata"). Resta il vincolo della modalità
  // 'required' che impedisce comunque l'avanzamento finché non c'è una
  // selezione esplicita (vedi `isProductRequired` + `goNext`).

  const goNext = useCallback(() => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx < 0 || idx >= STEP_ORDER.length - 1) return;
    // Task #525: dallo step 'service', se la modalità è 'required' e la
    // tipologia ha prodotti, blocca l'avanzamento finché non c'è una scelta.
    if (step === 'service' && isProductRequired() && !selectedProductId) {
      return;
    }
    let next = STEP_ORDER[idx + 1];
    // skip consultant step if not allowed
    if (next === 'consultant' && config && config.consultantFilterMode === 'any') {
      setSelectedConsultantId('any');
      setStep('slot');
      return;
    }
    setStep(next!);
  }, [step, config, consultants, isProductRequired, selectedProductId]);

  const goBack = useCallback(() => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx > 0) {
      let prev = STEP_ORDER[idx - 1];
      // Task #2846 — in modalità 'any' lo step consulente è saltato: tornando
      // indietro dallo step 'slot' si rientra sullo step 'date'.
      if (prev === 'consultant' && config && config.consultantFilterMode === 'any') {
        prev = 'date';
      }
      setStep(prev!);
    }
  }, [step, config]);

  const selectService = useCallback((id: string) => {
    setSelectedServiceId(id);
    setSelectedProductId('');
    setSelectedConsultantId('');
    setSelectedSlot(null);
    // Task #2786/#3040 — auto-selezione del prodotto unico in modalità 'auto'
    // (opzione unica): il dropdown non viene mostrato, quindi fissiamo il
    // singolo prodotto collegato così la prenotazione lo include comunque.
    if (config) {
      const svc = services.find((s) => s.id === id);
      const pid = autoSelectSingleProduct(config.serviceSelectionMode || 'auto', svc?.products || []);
      if (pid) setSelectedProductId(pid);
    }
  }, [config, services]);

  // Task #515 + #525: nei layout wizard (Doctolib) la pressione su una card
  // tipologia può sia selezionare-e-avanzare (quando non c'è dropdown inline
  // o non è obbligatorio), sia solo selezionare per mostrare il dropdown e
  // attendere il "Avanti" esplicito. Questa funzione risolve sincronicamente
  // dalla *id appena scelta* per evitare letture stale durante goNext.
  const selectServiceAndAdvance = useCallback((id: string) => {
    setSelectedServiceId(id);
    setSelectedProductId('');
    setSelectedConsultantId('');
    setSelectedSlot(null);
    if (!config) return;
    const svc = services.find((s) => s.id === id);
    const products = svc?.products || [];
    const mode = config.serviceSelectionMode || 'auto';
    // Task #2786/#3040 — il picker inline si mostra (e blocca l'avanzamento)
    // solo quando serve davvero una scelta (logica condivisa col modale admin).
    // In 'auto' con un solo prodotto (opzione unica) lo auto-selezioniamo e
    // avanziamo senza mostrare il dropdown; 'never' o tipologia senza prodotti
    // → avanza diretto.
    if (shouldShowProductPicker(mode, products)) {
      // resta su 'service' (l'utente sceglierà nella card e premerà Avanti)
      return;
    }
    const pid = autoSelectSingleProduct(mode, products);
    if (pid) setSelectedProductId(pid);
    // Task #2846 — dopo tipo/servizio si passa allo step 'data' (che precede il
    // consulente). Lo skip dello step consulente in modalità 'any' avviene in
    // goNext dallo step 'date'.
    setStep('date');
  }, [config, services]);

  const selectProduct = useCallback((id: string) => {
    setSelectedProductId(id);
  }, []);

  // Task #2786 — nei layout wizard (Doctolib) la scelta del prodotto nel
  // dropdown inline avanza da sola allo step successivo (niente più click su
  // "Avanti"). Risolve sincronicamente dall'id appena scelto per evitare letture
  // stale; salta lo step consulente quando la modalità è "qualsiasi".
  const selectProductAndAdvance = useCallback((id: string) => {
    setSelectedProductId(id);
    if (!config) return;
    // Task #2846 — il prodotto scelto avanza allo step 'data' (precede il
    // consulente); lo skip consulente in 'any' è gestito da goNext.
    setStep('date');
  }, [config]);

  const selectConsultant = useCallback((id: string) => {
    setSelectedConsultantId(id);
    setSelectedSlot(null);
  }, []);

  // Task #2752: selezione sede (solo quando config.allowStoreChoice).
  const selectStore = useCallback((id: string) => {
    setSelectedStoreId(id);
  }, []);

  // Auto-pick: se la scelta sede è attiva ma esiste una sola sede, la
  // preselezioniamo così l'utente non deve fare un click superfluo.
  useEffect(() => {
    if (!config || !config.allowStoreChoice) return;
    if (selectedStoreId) return;
    if (config.stores.length === 1) {
      setSelectedStoreId(config.stores[0]!.id);
    }
  }, [config, selectedStoreId]);

  const selectSlot = useCallback((slot: SlotItem) => {
    setSelectedSlot(slot);
  }, []);

  const updateCustomer = useCallback((patch: Partial<CustomerData>) => {
    setCustomer((prev) => ({ ...prev, ...patch }));
  }, []);

  const submitBooking = useCallback(async () => {
    if (!config || !selectedSlot || !selectedServiceId || !selectedConsultantId) {
      setError('Dati mancanti per la prenotazione');
      return null;
    }
    if (!customer.privacyAccepted) {
      setError('Devi accettare l\'informativa privacy');
      return null;
    }
    // Task #2752: se il form lascia scegliere la sede, è obbligatoria.
    if (config.allowStoreChoice && !selectedStoreId) {
      setError('Seleziona una sede');
      return null;
    }
    // Tutti i consensi GDPR dichiarati come "obbligatori" nel documento
    // privacy del tenant devono essere spuntati. La chiave 'privacy_policy'
    // è già coperta sopra; 'marketing' è opzionale per definizione anche
    // quando appare nella lista dei consensi (tracciato come opt-in).
    const requiredGdpr = (config.gdpr?.consents || []).filter(
      (c) => c.required && c.key !== 'privacy_policy' && c.key !== 'privacy' && c.key !== 'marketing',
    );
    for (const c of requiredGdpr) {
      if (!customer.gdprConsents?.[c.key]) {
        setError(`Devi accettare: ${c.label}`);
        return null;
      }
    }
    if (preview) {
      const fake: BookingResult = {
        appointmentId: 'preview',
        cancelToken: 'preview',
        status: config.rules.autoConfirm ? 'confirmed' : 'pending',
      };
      setBookingResult(fake);
      setStep('confirm');
      return fake;
    }
    setLoadingKey('booking', true);
    setError(null);
    setCaptchaError(null);
    try {
      const utm: Record<string, string> = {};
      if (typeof window !== 'undefined') {
        const sp = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
          const v = sp.get(k);
          if (v) utm[k] = v;
        });
      }
      const result = await appointmentPublicApi.book(
        slug,
        {
          serviceId: selectedServiceId,
          productId: selectedProductId || undefined,
          storeId: config.allowStoreChoice ? (selectedStoreId || undefined) : undefined,
          consultantId: selectedConsultantId,
          slot: { start: selectedSlot.start, end: selectedSlot.end },
          customer,
          utm,
          honeypotFieldName: config.honeypotFieldName,
          honeypotValue: honeypot,
          // Task #1211: invia id+versione del documento privacy attualmente
          // mostrato all'utente. Il backend lo confronta con la versione
          // attiva e respinge il booking se l'utente ha un form cached.
          privacyDocumentId: config.gdpr?.document?.id,
          privacyDocumentVersion: config.gdpr?.document?.version,
        },
        { ...apiOptions, captchaToken },
      );
      setBookingResult(result);
      setStep('confirm');
      return result;
    } catch (e) {
      const errAny = e as Error & { code?: string; status?: number };
      const code = errAny?.code;
      if (code === 'CAPTCHA_FAILED' || code === 'CAPTCHA_MISSING') {
        setCaptchaError(code === 'CAPTCHA_MISSING' ? 'missing' : 'failed');
        setCaptchaToken(undefined);
        try { captchaResetRef.current?.(); } catch { /* ignore */ }
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : 'Errore durante la prenotazione');
      }
      return null;
    } finally {
      setLoadingKey('booking', false);
    }
  }, [
    config,
    selectedSlot,
    selectedServiceId,
    selectedProductId,
    selectedStoreId,
    selectedConsultantId,
    customer,
    slug,
    apiOptions,
    captchaToken,
    honeypot,
    preview,
    setLoadingKey
  ]);

  const reset = useCallback(() => {
    setSelectedServiceId('');
    setSelectedProductId('');
    setSelectedConsultantId('');
    setSelectedStoreId('');
    setSelectedSlot(null);
    setBookingResult(null);
    setError(null);
    setStep('service');
    setCustomer({ name: '', firstName: '', lastName: '', email: '', phone: '', privacyAccepted: false, marketingAccepted: false, customAnswers: {} });
  }, []);

  const selectedService = useMemo(
    () => services.find((s) => s.id === selectedServiceId) || null,
    [services, selectedServiceId],
  );
  const selectedProduct = useMemo(
    () => (productsForSelectedService || []).find((p) => p.id === selectedProductId) || null,
    [productsForSelectedService, selectedProductId],
  );
  const selectedConsultant = useMemo(
    () => consultants.find((c) => c.id === selectedConsultantId) || null,
    [consultants, selectedConsultantId],
  );
  const selectedStore = useMemo(
    () => (config?.stores || []).find((s) => s.id === selectedStoreId) || null,
    [config, selectedStoreId],
  );

  return {
    config,
    setConfig,
    services,
    consultants,
    slots,
    selectedDate,
    setSelectedDate,
    selectedServiceId,
    selectedProductId,
    selectedConsultantId,
    selectedStoreId,
    selectedSlot,
    selectedService,
    selectedProduct,
    selectedConsultant,
    selectedStore,
    productsForSelectedService,
    shouldShowProductStep,
    isProductRequired,
    step,
    setStep,
    currentStepIndex,
    customer,
    updateCustomer,
    honeypot,
    setHoneypot,
    captchaToken,
    setCaptchaToken,
    /** @deprecated Use `captchaToken` instead. */
    hcaptchaToken: captchaToken,
    /** @deprecated Use `setCaptchaToken` instead. */
    setHcaptchaToken: setCaptchaToken,
    loading,
    error,
    setError,
    captchaError,
    setCaptchaError,
    registerCaptchaReset,
    resetCaptcha,
    bookingResult,
    selectService,
    selectServiceAndAdvance,
    selectProduct,
    selectProductAndAdvance,
    selectConsultant,
    selectStore,
    selectSlot,
    submitBooking,
    goNext,
    goBack,
    reset,
  };
}

export type AppointmentFormState = ReturnType<typeof useAppointmentForm>;
